import type { Position } from "./game.types"
import type { AttackType, DefenseType, DamageRange, UnitShrineBuffs } from "./hero.types"

export type SummonType = "wolf" | "fox" | "boar" | "siege-tower"

// Random turret names for the Siegesmith's Forge Turret
export const TURRET_NAMES = [
  "Grudgebeam",
  "Molten Verdict",
  "Cinder Warden",
  "Bronzeblast",
  "Runesearer",
  "Hearthfire Mk. IV",
] as const

export interface SummonedUnit {
  id: string
  type: SummonType
  name?: string // Custom name (used for turrets with random names)
  ownerId: string // Hero ID that summoned this
  position: Position
  targetPosition: Position | null
  health: number
  maxHealth: number
  damage: DamageRange
  damageType: AttackType
  armorType: DefenseType
  armor: number
  attackRange: number
  attackSpeed: number
  attackCooldown: number
  speed: number
  rotation: number
  isMoving: boolean
  isAttacking: boolean
  target: string | null // Enemy ID
  color: string
  size: number
  // For towers only
  isTower: boolean
  range: number
  projectileSpeed: number
  // Animation state (for turrets)
  hurtEndTime?: number // When hurt animation should end
  spawnTime: number // Timestamp when summon was created (for fade-in animation)
  level: number // Skill level that created this summon
  // Shrine buffs (from heroic shrine aura)
  shrineBuffs: UnitShrineBuffs
}

export interface SummonConfig {
  type: SummonType
  displayName: string
  health: number
  damage: DamageRange
  damageType: AttackType
  armorType: DefenseType
  armor: number
  attackRange: number
  attackSpeed: number
  speed: number
  color: string
  size: number
  // Tower-specific
  isTower: boolean
  range: number
  projectileSpeed: number
}

// Default configs for summon types
export const summonConfigs: Record<SummonType, SummonConfig> = {
  wolf: {
    type: "wolf",
    displayName: "Silver Wolf",
    health: 80,
    damage: { min: 12, max: 18 },
    damageType: "normal",
    armorType: "medium",
    armor: 2,
    attackRange: 35,
    attackSpeed: 40,
    speed: 3.6, // 2x hero ranger speed
    color: "#C0C0C0", // Silver
    size: 28,
    isTower: false,
    range: 0,
    projectileSpeed: 0,
  },
  fox: {
    type: "fox",
    displayName: "Briar",
    health: 60, // Weaker than wolf
    damage: { min: 6, max: 10 }, // Lower damage (reduced from 8-14)
    damageType: "pierce",
    armorType: "light",
    armor: 0,
    attackRange: 32,
    attackSpeed: 30, // Faster attacks
    speed: 4.5, // Faster than wolf
    color: "#D2691E", // Chocolate/orange
    size: 24, // Smaller
    isTower: false,
    range: 0,
    projectileSpeed: 0,
  },
  boar: {
    type: "boar",
    displayName: "Bramble",
    health: 150, // Tankier (increased from 120)
    damage: { min: 15, max: 22 }, // Higher damage
    damageType: "normal",
    armorType: "heavy",
    armor: 4,
    attackRange: 38,
    attackSpeed: 50, // Slower attacks
    speed: 2.2, // Slower (reduced from 2.8)
    color: "#8B4513", // Saddle brown
    size: 32, // Bigger
    isTower: false,
    range: 0,
    projectileSpeed: 0,
  },
  "siege-tower": {
    type: "siege-tower",
    displayName: "Forge Turret",
    health: 200,
    damage: { min: 1, max: 2 },       // Small per-tick damage for continuous laser (50% reduced)
    damageType: "magic",              // Magic damage type
    armorType: "fortified",
    armor: 5,
    attackRange: 150,
    attackSpeed: 6,                      // ~100ms tick interval for rapid damage
    speed: 0, // Towers don't move
    color: "#CD7F32", // Bronze
    size: 40,
    isTower: true,
    range: 150,
    projectileSpeed: 0,               // No projectiles - uses laser beam
  },
}
